/**
 * Price Formatting Utilities
 *
 * Functions for formatting prices for display
 */

import type { Money } from '../../../types';

/**
 * Format price with locale options
 */
export function formatPriceWithLocale({
    amount,
    currencyCode,
    locale,
}: {
    amount: number;
    currencyCode: string;
    locale: string;
}): string {
    const formatCurrency = new Intl.NumberFormat(locale, {
        style: 'currency',
        currency: currencyCode,
        currencyDisplay: 'code',
    });

    if (currencyCode === 'VND') {
        const formattedAmount = formatCurrency.format(amount);
        return formattedAmount.replace('VND', 'đ').replace(/\s/g, '');
    }

    return formatCurrency.format(amount);
}

/**
 * Format price from Money object
 * This is the primary price formatting function used throughout the app
 *
 * @param money - Money object with value and currency
 * @returns Formatted price string
 */
export function formatPrice(money: Money | { value: number; currency?: string | null } | null | undefined): string {
    if (!money) {
        return '0 ₫';
    }

    const value = money.value ?? 0;
    const currency = money.currency ?? 'VND';

    return new Intl.NumberFormat('vi-VN', {
        style: 'currency',
        currency: currency,
    }).format(value);
}

/**
 * Format variant price with discount info
 */
export function formatVariantPrice({
    amount,
    baseAmount,
    currencyCode,
    locale,
}: {
    baseAmount: number;
    amount: number;
    currencyCode: string;
    locale: string;
}): { price: string; basePrice: string | null; discount: string | null } {
    const hasDiscount = baseAmount > amount;
    const formatDiscount = new Intl.NumberFormat(locale, { style: 'percent' });
    const discount = hasDiscount ? formatDiscount.format((baseAmount - amount) / baseAmount) : null;

    const price = formatPriceWithLocale({ amount, currencyCode, locale });
    const basePrice = hasDiscount ? formatPriceWithLocale({ amount: baseAmount, currencyCode, locale }) : null;

    return { price, basePrice, discount };
}

/**
 * Price calculation result
 */
export interface PriceResult {
    price: string;
    basePrice: string | null;
    discount: string | null;
}

/**
 * Calculate price formatting (non-hook version for SSR)
 */
export function calculatePrice(
    data?: { amount: number; baseAmount?: number; currencyCode?: string } | null
): PriceResult {
    const currency = 'VND';
    const locale = 'vi';
    let currencyCode = data?.currencyCode || 'VND';
    const { amount, baseAmount } = { ...data };

    if (currencyCode === undefined) {
        currencyCode = currency || 'VND';
    }

    if (typeof amount !== 'number' || !currencyCode) {
        return { price: '', basePrice: null, discount: null };
    }

    const currentLocale = locale || 'vi';
    const value = baseAmount
        ? formatVariantPrice({
              amount,
              baseAmount,
              currencyCode,
              locale: currentLocale,
          })
        : formatPriceWithLocale({ amount, currencyCode, locale: currentLocale });

    return typeof value === 'string' ? { price: value, basePrice: null, discount: null } : value;
}

/**
 * Format a numeric price value for display (simple version)
 *
 * @param price - The numeric price value
 * @param currency - Currency code (default: 'VND')
 * @returns Formatted price string
 */
export const formatPriceValue = (price: number | null | undefined, currency: string = 'VND'): string => {
    if (price === null || price === undefined) {
        return '0 ₫';
    }

    return new Intl.NumberFormat('vi-VN', {
        style: 'currency',
        currency: currency,
    }).format(price);
};
